import XLSX from 'xlsx-js-style'
import { useEffect } from 'react';
import { Download16 } from '../../../images/icons/icons_theme';

/** 
 * @공통Props
    <ExcelDownload 
        excelHead = {excelHead} //dataHead와 엑셀 헤더의 폼이 같으면 dataHead 사용
        excelData = {excelData} //fetchData로 불러온 데이터 혹은 엑셀에 들어가야 하는 불러온 데이터
        searched = {isSearched}
        dataCode = {dataColumn} //dataColumn
        excelFileName = {excelFileName}
        mergeData = {mergeData} //해당 페이지에 mergeConfig가 존재하면 할당
    />
 * 
 * **/

const ExcelDownload = ({ excelHead, excelData, searched, dataCode, excelFileName, mergeData, confirmYn, openConfModal, isDown }) => {
    const headerRows = Array.isArray(excelHead[0]) ? excelHead : [excelHead];
    const headerRowCount = headerRows.length;

    const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

    // 헤더 병합 정보 계산
    const rows = headerRows.length;
    const cols = headerRows[0].length;
    const singleRow = rows === 1;
    const skip = Array.from({ length: rows }, () => Array(cols).fill(false));

    const getSpans = (r, c) => {
        if (skip[r][c]) return null;

        const cellValue = headerRows[r][c];
        let rowspan = 1;
        let colspan = 1;

        for (let i = c + 1; i < cols; i++) {
            if (headerRows[r][i] === cellValue) {
                colspan++;
                skip[r][i] = true;
            } else break;
        }

        if (!singleRow) {
            for (let i = r + 1; i < rows; i++) {
                let same = true;
                for (let j = 0; j < colspan; j++) {
                    if (headerRows[i][c + j] !== cellValue) {
                        same = false;
                        break;
                    }
                }
                if (same) {
                    for (let j = 0; j < colspan; j++) {
                        skip[i][c + j] = true;
                    }
                    rowspan++;
                } else break;
            }
        }

        return { rowspan, colspan, value: cellValue };
    };

    const headerMerges = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const span = getSpans(r, c);
            if (!span) continue;
            const { rowspan, colspan } = span;
            if (rowspan > 1 || colspan > 1) {
                headerMerges.push({
                    s: { r, c },
                    e: { r: r + rowspan - 1, c: c + colspan - 1 },
                });
            }
        }
    }

    // 데이터 행 추가
    excelData.forEach(data => {
        const row = dataCode.map(key => data[key]);
        XLSX.utils.sheet_add_aoa(worksheet, [row], { origin: -1 });
    });

    // 데이터 병합 정보 계산
    const mergeConfig = (mergeData ?? []).map(cfg => ({
        ...cfg,
        nullable: cfg.nullable ?? false,
    }));

    const computeRowSpans = (dataList, mergeConfig = [], allColumns = []) => {
        const rowSpans = dataList.map(() => ({}));

        mergeConfig.forEach(({ key: colKey, row, nullable }) => {
            let prevValue = null;
            let startIndex = 0;
            let mergeCount = 1;

            for (let i = 0; i < dataList.length; i++) {
                const currentValue = dataList[i][colKey];
                const isMergeable = nullable
                    ? currentValue === prevValue
                    : (currentValue === prevValue && currentValue !== '' && currentValue !== null);

                if (isMergeable) {
                    if (row == null || mergeCount < row) {
                        rowSpans[startIndex][colKey] += 1;
                        rowSpans[i][colKey] = 0;
                        mergeCount += 1;
                    } else {
                        rowSpans[i][colKey] = 1;
                        startIndex = i;
                        mergeCount = 1;
                    }
                } else {
                    rowSpans[i][colKey] = 1;
                    startIndex = i;
                    mergeCount = 1;
                    prevValue = currentValue;
                }
            }
        });

        dataList.forEach((_, i) => {
            allColumns.forEach(colKey => {
                if (!(colKey in rowSpans[i])) {
                    rowSpans[i][colKey] = 1;
                }
            });
        });

        return rowSpans;
    };

    const rowSpans = mergeData ? computeRowSpans(excelData, mergeConfig, dataCode) : null;

    const dataMerges = [];
    if (rowSpans) {
        rowSpans.forEach((rowSpan, rowIdx) => {
            dataCode.forEach((colKey, colIdx) => {
                const span = rowSpan[colKey];
                if (span > 1) {
                    dataMerges.push({
                        s: { r: headerRowCount + rowIdx, c: colIdx },
                        e: { r: headerRowCount + rowIdx + span - 1, c: colIdx },
                    });
                }
            });
        });
    }

    // 병합 정보 적용
    worksheet['!merges'] = [...headerMerges, ...dataMerges];

    // 셀 스타일 적용
    for (const addr in worksheet) {
        if (typeof worksheet[addr] !== 'object') continue;
        const cell = XLSX.utils.decode_cell(addr);

        worksheet[addr].s = {
            font: { name: 'Arial' },
            border: {
                top: { style: 'thin', color: { rgb: 'D9D9D9' } },
                bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
                left: { style: 'thin', color: { rgb: 'D9D9D9' } },
                right: { style: 'thin', color: { rgb: 'D9D9D9' } },
            },
            alignment: {
                vertical: 'center',
                horizontal: 'center',
                wrapText: true,
            },
        };

        // 헤더 배경색
        if (cell.r < headerRowCount) {
            worksheet[addr].s.fill = {
                patternType: 'solid',
                fgColor: { rgb: 'f2f4f7' },
            };
        }
    }

    const executeExcelDownload = () => {
        if (excelData?.length > 0) {
            worksheet['!cols'] = dataCode.map(() => ({ wpx: 230 }));
        }

        // 통합 문서는 누를 때마다 새로 만든다(화면이 다시 그려지지 않은 채 다시 누르면 같은 이름 시트가 겹쳐 오류)
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '통합 문서1');
        XLSX.writeFile(workbook, `${excelFileName}.xlsx`);
    };

    const excelDownload = () => {
        if (confirmYn) {
            openConfModal();
            return;
        };
        executeExcelDownload();
    };

    useEffect(() => {
        if (isDown) {
            executeExcelDownload();
        }
    }, [isDown]);

    return (
        <button
            disabled={!(searched && excelData.length)}
            onClick={excelDownload}
            className='excel_btn'
        >
            <Download16 className='co_white' />
            엑셀다운로드
        </button>
    );
};

export default ExcelDownload;